/* Jouly homepage behaviour. Content is rendered in Liquid; this only enhances. */
(() => {
  const reduced = document.documentElement.classList.contains('j-reduced');
  const desktop = window.matchMedia('(min-width: 750px)');

  /* Scroll reveal */
  const reveal = () => {
    const els = document.querySelectorAll('[data-j-reveal]:not(.is-in), .j-step:not(.is-in)');
    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
  };

  /* Header: compact state once scrolled */
  const header = () => {
    const h = document.querySelector('[data-j-header]');
    if (!h) return;
    const ann = h.querySelector('.j-announce');
    const setAnn = () => document.documentElement.style.setProperty('--j-ann', `${ann ? ann.offsetHeight : 0}px`);
    setAnn();
    if (ann) new ResizeObserver(setAnn).observe(ann);
    let ticking = false;
    const update = () => {
      h.classList.toggle('is-scrolled', window.scrollY > 24);
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  };

  /* Mobile menu */
  const menu = () => {
    const panel = document.querySelector('[data-j-menu]');
    const openBtn = document.querySelector('[data-j-menu-open]');
    if (!panel || !openBtn) return;
    const open = (e) => {
      const ann = document.querySelector('.j-announce');
      panel.style.setProperty('--j-menu-top', `${ann ? Math.max(0, ann.getBoundingClientRect().bottom) : 0}px`);
      panel.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-open')));
      document.body.classList.add('j-menu-open');
      openBtn.setAttribute('aria-expanded', 'true');
      if (e && e.detail === 0) panel.querySelector('[data-j-menu-close]')?.focus();
    };
    const close = () => {
      panel.classList.add('is-closing');
      panel.classList.remove('is-open');
      document.body.classList.remove('j-menu-open');
      openBtn.setAttribute('aria-expanded', 'false');
      setTimeout(() => { panel.hidden = true; panel.classList.remove('is-closing'); }, reduced ? 0 : 600);
      openBtn.focus();
    };
    openBtn.addEventListener('click', open);
    panel.querySelectorAll('[data-j-menu-close]').forEach((b) => b.addEventListener('click', close));
    panel.querySelectorAll('[data-j-menu-close-link]').forEach((a) => a.addEventListener('click', () => { if (a.getAttribute('href')?.startsWith('#')) close(); }));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) close(); });
    desktop.addEventListener('change', (e) => { if (e.matches && !panel.hidden) close(); });
  };

  /* Videos: only the breakpoint that shows them loads and plays */
  const videos = () => {
    const vids = [...document.querySelectorAll('video[data-j-video]')];
    if (!vids.length) return;
    const visibleNow = (v) => v.offsetParent !== null || getComputedStyle(v).display !== 'none';
    const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting && visibleNow(v)) {
          if (!v.src) v.src = v.dataset.src;
          if (!reduced) v.play().catch(() => {});
        } else if (!v.paused) v.pause();
      });
    }, { rootMargin: '200px' }) : null;
    vids.forEach((v) => io ? io.observe(v) : (v.src = v.dataset.src));
    desktop.addEventListener('change', () => vids.forEach((v) => { if (!visibleNow(v)) v.pause(); io?.unobserve(v); io?.observe(v); }));
  };

  /* Timeline: draw the rising line through the dots (desktop) */
  const timeline = () => {
    document.querySelectorAll('[data-j-timeline]').forEach((tl) => {
      const svg = tl.querySelector('.j-tl__line');
      const path = svg?.querySelector('path');
      if (!path) return;
      const draw = () => {
        if (!desktop.matches) return;
        const box = tl.getBoundingClientRect();
        const pts = [...tl.querySelectorAll('[data-j-tl-dot]')].map((d) => {
          const r = d.getBoundingClientRect();
          return [r.left - box.left + r.width / 2, r.top - box.top + r.height / 2];
        });
        if (pts.length < 2) return;
        const all = [[0, pts[0][1] + 50], ...pts, [box.width, pts[pts.length - 1][1] - 40]];
        let d = `M ${all[0][0]} ${all[0][1]}`;
        for (let i = 1; i < all.length; i++) {
          const [x0, y0] = all[i - 1];
          const [x1, y1] = all[i];
          const mx = (x0 + x1) / 2;
          d += ` C ${mx} ${y0} ${mx} ${y1} ${x1} ${y1}`;
        }
        svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
        path.setAttribute('d', d);
        path.style.setProperty('--len', Math.ceil(path.getTotalLength()));
      };
      draw();
      new ResizeObserver(draw).observe(tl);
    });
  };

  /* Banner parallax: small, transform only, only while on screen */
  const parallax = () => {
    if (reduced) return;
    const frames = [...document.querySelectorAll('[data-j-parallax]')];
    if (!frames.length) return;
    const onScreen = new Set();
    const io = new IntersectionObserver((es) => es.forEach((e) => (e.isIntersecting ? onScreen.add(e.target) : onScreen.delete(e.target))));
    frames.forEach((f) => io.observe(f));
    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      onScreen.forEach((f) => {
        const r = f.getBoundingClientRect();
        const p = (r.top + r.height / 2 - vh / 2) / vh;
        f.style.setProperty('--py', `${(p * -40).toFixed(1)}px`);
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  };

  /* Marquee: repeat the run until the row always overfills the screen, then loop by exactly one run */
  const marquee = () => {
    document.querySelectorAll('.j-marquee').forEach((mq) => {
      const track = mq.querySelector('.j-marquee__track');
      const run = track?.querySelector('.j-marquee__run');
      if (!run) return;
      const fill = () => {
        const w = run.getBoundingClientRect().width;
        if (!w) return;
        const need = Math.ceil(mq.clientWidth / w) + 1;
        const runs = track.querySelectorAll('.j-marquee__run');
        for (let i = runs.length; i < need; i++) {
          const c = run.cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          c.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
          track.appendChild(c);
        }
        track.style.setProperty('--run', `${w}px`);
      };
      fill();
      if (!mq.dataset.jMq) { mq.dataset.jMq = '1'; new ResizeObserver(fill).observe(mq); }
    });
  };

  /* Smooth scroll: same-page links (#join, /#products) glide with an ease-in-out, stopping below the sticky bar */
  const smoothScroll = () => {
    if (document.documentElement.dataset.jSmooth) return;
    document.documentElement.dataset.jSmooth = '1';
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href*="#"]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      const url = new URL(a.href, location.href);
      if (url.pathname !== location.pathname || url.origin !== location.origin || url.hash.length < 2) return;
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!target) return;
      e.preventDefault();
      const menuClose = document.body.classList.contains('j-menu-open') && document.querySelector('[data-j-menu-close]');
      if (menuClose) menuClose.click();
      requestAnimationFrame(() => {
        const bar = document.querySelector('.j-header__wrap');
        const offset = bar ? bar.getBoundingClientRect().height + 16 : 0;
        const start = window.scrollY;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const toBottom = target.id === 'join' || target.hasAttribute('data-j-scroll-bottom');
        const end = toBottom ? max : Math.min(max, Math.max(0, start + target.getBoundingClientRect().top - offset));
        const dist = end - start;
        history.pushState(null, '', url.hash);
        if (reduced || Math.abs(dist) < 2) { window.scrollTo({ top: end, behavior: 'instant' }); return; }
        const duration = Math.min(1600, Math.max(700, Math.abs(dist) * 0.35));
        let t0 = null;
        const step = (now) => {
          if (t0 === null) t0 = now;
          const t = Math.min(1, (now - t0) / duration);
          const goal = toBottom ? document.documentElement.scrollHeight - window.innerHeight : end;
          window.scrollTo({ top: start + (goal - start) * ease(t), behavior: 'instant' });
          if (t < 1) requestAnimationFrame(step);
          else if (toBottom) setTimeout(() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'instant' }), 120);
        };
        requestAnimationFrame(step);
      });
    });
  };

  /* FAQ: opening one question closes the others (fallback for browsers without exclusive <details name>) */
  const faq = () => {
    document.querySelectorAll('.j-faq__list').forEach((list) => {
      list.addEventListener('toggle', (e) => {
        const d = e.target;
        if (!(d instanceof HTMLDetailsElement) || !d.open) return;
        list.querySelectorAll('details[open]').forEach((o) => { if (o !== d) o.open = false; });
      }, true);
    });
  };

  const init = () => { reveal(); header(); menu(); videos(); timeline(); parallax(); marquee(); smoothScroll(); faq(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', () => { reveal(); timeline(); videos(); marquee(); });
})();
